import { styled } from '@mui/material';

import { CAST_AI } from '@cast/constants';
import {
  Divider,
  IconButtonProps,
  Icons,
  TooltipProps,
  EntityAction,
  EntityActions,
  useDestructiveDialog,
} from '@cast/design-system';
import { K8sProvider } from '@cast/shared-types';
import { AgentStatus, Cluster, ClusterStatus } from '@cast/types';
import { isManagedByTerraform } from '@cast/utils';

import { LogoSmallSvg } from 'assets/svg';
import { useAbility } from '@cast/shared-ability-data-access';
import { CLUSTER_TOOLTIPS } from 'features/cluster/constants';
import { useInstallKentScriptDialog } from 'features/kent/InstallKentScript';
import {
  useAssignCredentialsDialogV2,
  useDisconnectPhase1ClusterDialog,
  useDisconnectPhase2ClusterDialog,
  useHibernateClusterDialog,
  useReconnectClusterDialog,
  useResumeClusterDialog,
} from 'hooks/dialogs';
import { useAdjustClusterCostsDialog } from 'hooks/dialogs/cluster/useAdjustClusterCostsDialog';
import {
  useDeleteClusterMutation,
  useReconcileClusterMutation,
} from 'hooks/mutations';
import { useDeleteCluster } from 'hooks/useDeleteCluster';
import { useGetClusterActionsVisibility } from 'hooks/useGetClusterActionsVisibility';

import {
  isClusterManaged,
  isKentManagedCluster,
} from '@cast/shared-cluster-context-data-access';

const CastLogoWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
  padding: '2px 3px',
});

type ClusterActionsMenuProps = {
  cluster: Cluster;
  triggerButtonProps?: IconButtonProps;
  limitActions?: boolean;
};

export const ClusterActionsMenu = ({
  cluster,
  limitActions,
  triggerButtonProps,
}: ClusterActionsMenuProps) => {
  const { mutate: deleteClusterMutation } = useDeleteClusterMutation(cluster);
  const { openDialog: openRemoveFromCastAiDialog } = useDestructiveDialog();

  const { mutate: reconcileClusterMutation } = useReconcileClusterMutation(
    cluster.id
  );

  const { openAssignCredentialsDialog } = useAssignCredentialsDialogV2();

  const isTerraformManaged = isManagedByTerraform(cluster.managedBy);

  const [canEditClusters, canDeleteClusters] = useAbility().canMany(
    [
      ['update', 'Clusters'],
      ['delete', 'Clusters'],
    ],
    { clusterId: cluster.id }
  );
  const [canViewHibernateClusters, canHibernateClusters, canResumeClusters] =
    useAbility().canMany(
      [
        ['view', 'OrganizationClusterHibernate'],
        ['create', 'OrganizationClusterHibernate'],
        ['update', 'OrganizationClusterHibernate'],
      ],
      { clusterId: cluster.id }
    );

  const canEdit = canEditClusters && !isTerraformManaged;

  const canDelete =
    [AgentStatus.DISCONNECTED, AgentStatus.CONNECTING].includes(
      cluster.agentStatus
    ) &&
    ![
      ClusterStatus.DELETING,
      ClusterStatus.HIBERNATING,
      ClusterStatus.RESUMING,
    ].includes(cluster.status);

  const canDisconnect =
    [AgentStatus.ONLINE, AgentStatus.NOT_RESPONDING].includes(
      cluster.agentStatus
    ) && !isTerraformManaged;

  const { openReconnectDialog } = useReconnectClusterDialog();
  const { openAdjustClusterCostsDialog } = useAdjustClusterCostsDialog();
  const { openDisconnectPhase1ClusterDialog } =
    useDisconnectPhase1ClusterDialog();
  const { openDisconnectPhase2ClusterDialog } =
    useDisconnectPhase2ClusterDialog();
  const { openHibernateClusterDialog } = useHibernateClusterDialog();
  const { openResumeClusterDialog } = useResumeClusterDialog();
  const { deleteCluster } = useDeleteCluster(cluster);
  const { showInstallKentScript } = useInstallKentScriptDialog();

  const clusterActionVisibility = useGetClusterActionsVisibility(cluster);
  const noVisibleActions = Object.values(clusterActionVisibility).every(
    (action) => !action
  );

  if (noVisibleActions) {
    return null;
  }

  const isManaged = isClusterManaged(cluster);
  const {
    disconnect: showDisconnect,
    delete: showDelete,
    reconnect: showReconnect,
    reconcile: showReconcile,
    remove: showRemove,
    adjustCosts: showAdjustCosts,
    hibernate: showHibernate,
    resume: showResume,
    features: showFeatures,
  } = clusterActionVisibility;

  const handleReconnectCluster = () => {
    openReconnectDialog(cluster);
  };

  const handleReconcileCluster = () => {
    reconcileClusterMutation();
  };

  const handleDeleteCluster = () => {
    deleteCluster();
  };

  const handleDisconnectCluster = () => {
    if (isManaged && cluster.providerType !== K8sProvider.ANYWHERE) {
      openDisconnectPhase2ClusterDialog(cluster);
    } else {
      openDisconnectPhase1ClusterDialog(cluster);
    }
  };

  const handleRemoveFromCastAi = () => {
    openRemoveFromCastAiDialog({
      title: `Remove ${cluster.name} from ${CAST_AI}?`,
      message:
        'The cluster will stay operational, but will be removed from the console.',
      confirmButtonText: 'Remove',
      onConfirm: () => {
        deleteClusterMutation();
      },
    });
  };

  const handleHibernateCluster = () => {
    openHibernateClusterDialog(cluster);
  };

  const handleResumeCluster = () => {
    openResumeClusterDialog(cluster);
  };

  const actionRequiresTerraformTooltipProps: Omit<TooltipProps, 'children'> = {
    title: CLUSTER_TOOLTIPS.actionRequiresTerraform,
    placement: 'left',
    sx: { width: 196 },
    wrapIf: isTerraformManaged,
  };

  return (
    <EntityActions
      triggerButtonProps={triggerButtonProps}
      testId="cluster-actions-select"
    >
      {!limitActions && showFeatures && (
        <EntityAction
          tooltipProps={actionRequiresTerraformTooltipProps}
          disabled={!canEdit}
          onClick={() => {
            if (isKentManagedCluster(cluster)) {
              showInstallKentScript({ cluster });
            } else {
              openAssignCredentialsDialog({ cluster });
            }
          }}
          startAdornment={
            <CastLogoWrapper>
              <LogoSmallSvg />
            </CastLogoWrapper>
          }
          testId="open-features-action"
        >
          {CAST_AI} features
        </EntityAction>
      )}

      {!limitActions && showReconnect && (
        <EntityAction
          tooltipProps={actionRequiresTerraformTooltipProps}
          disabled={!canEdit}
          onClick={handleReconnectCluster}
          startAdornment={<Icons.PlugsConnected />}
          testId="reconnect-cluster-action"
        >
          Reconnect
        </EntityAction>
      )}

      {!limitActions && showReconcile && (
        <EntityAction
          disabled={!canEditClusters}
          onClick={handleReconcileCluster}
          startAdornment={<Icons.ArrowCounterClockwise />}
          testId="reconcile-cluster-action"
        >
          Trigger reconcile
        </EntityAction>
      )}

      {!limitActions && showHibernate && canViewHibernateClusters && (
        <EntityAction
          disabled={!canHibernateClusters || !canEditClusters}
          onClick={handleHibernateCluster}
          startAdornment={<Icons.PauseCircle />}
          testId="hibernate-cluster-action"
        >
          Hibernate cluster
        </EntityAction>
      )}

      {!limitActions && showResume && canViewHibernateClusters && (
        <EntityAction
          disabled={!canResumeClusters || !canEditClusters}
          onClick={handleResumeCluster}
          startAdornment={<Icons.PlayCircle />}
          testId="resume-cluster-action"
        >
          Resume cluster
        </EntityAction>
      )}

      {!limitActions && showAdjustCosts && (
        <EntityAction
          onClick={() => openAdjustClusterCostsDialog({ cluster })}
          startAdornment={<Icons.FadersHorizontal />}
          testId="adjust-costs-action"
        >
          Adjust costs
        </EntityAction>
      )}

      <Divider variant="fullWidth" />

      {showDisconnect && (
        <EntityAction
          tooltipProps={actionRequiresTerraformTooltipProps}
          disabled={!canDisconnect || !canDeleteClusters}
          onClick={handleDisconnectCluster}
          startAdornment={<Icons.XCircle />}
          disruptive
          testId="disconnect-cluster-action"
        >
          Disconnect cluster
        </EntityAction>
      )}

      {showDelete && (
        <EntityAction
          disabled={!canDelete || !canDeleteClusters}
          onClick={handleDeleteCluster}
          startAdornment={<Icons.TrashSimple />}
          disruptive
          testId="delete-cluster-action"
        >
          Remove cluster
        </EntityAction>
      )}

      {showRemove && (
        <EntityAction
          disabled={!canDeleteClusters}
          onClick={handleRemoveFromCastAi}
          startAdornment={<Icons.TrashSimple />}
          disruptive
        >
          Remove from {CAST_AI}
        </EntityAction>
      )}
    </EntityActions>
  );
};
